import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, UserManagementUser } from '../types';
import { getApiUrl } from '../config';
import { AddUserModal } from './AddUserModal';
import { getCompletionColor, calculateCaseFormCompletion, countCaseMeetings, calculateOverallProcessCompletion, calculateMeetingCompletion } from '../utils/intakeCompletion';
import { canSeeSitemap } from '../utils/roleUtils';

interface Organization {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  administrators: string[]; // Array of user IDs
  administratorNames: string[]; // Array of user names for display
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
  requiredMeetings?: number; // Number of meetings required to complete the process
}

interface OrganizationsProps {
  user: User;
}

type WizardStep = 'basic' | 'administrators' | 'details';

export default function Organizations({ user }: OrganizationsProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsViewMode, setDetailsViewMode] = useState<'view' | 'edit'>('view');
  const [showAddMember, setShowAddMember] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [allUsers, setAllUsers] = useState<UserManagementUser[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [organizationCompletions, setOrganizationCompletions] = useState<{ [organizationId: string]: number }>({});
  const [organizationMeetingCompletions, setOrganizationMeetingCompletions] = useState<{ [organizationId: string]: { completed: number; total: number } }>({});
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    administrators: [] as string[],
    administratorNames: [] as string[]
  });

  // User search
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserManagementUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Details modal state
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    administrators: [] as string[],
    administratorNames: [] as string[],
    allMembers: [] as string[] // All users with this organizationId
  });
  const [detailsUserSearchTerm, setDetailsUserSearchTerm] = useState('');
  const [detailsSearchResults, setDetailsSearchResults] = useState<UserManagementUser[]>([]);
  const [isDetailsSearching, setIsDetailsSearching] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // Load all users for member display
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const users: UserManagementUser[] = [];
        
        snapshot.forEach((doc) => {
          const userData = doc.data();
          if (!userData.deleted) {
            users.push({
              id: doc.id,
              name: userData.name || '',
              email: userData.email || '',
              role: userData.role,
              organizationId: userData.organizationId,
              organizationRole: userData.organizationRole,
              createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
              lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
              loginCount: userData.loginCount || 0,
              blocked: userData.blocked || false
            });
          }
        });
        setAllUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Subscribe to organizations in real-time
  useEffect(() => {
    const organizationsRef = collection(db, 'organizations');
    const q = query(organizationsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orgs: Organization[] = [];
      snapshot.forEach((doc) => {
        orgs.push({ id: doc.id, ...doc.data() } as Organization);
      });
      setOrganizations(orgs);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load programs
  useEffect(() => {
    const q = query(collection(db, 'programs'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPrograms(programsData);
    });

    return () => unsubscribe();
  }, []);

  // Load groups
  useEffect(() => {
    const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const groupsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsData);
    });

    return () => unsubscribe();
  }, []);

  // Calculate organization completion statistics based on programs and groups
  useEffect(() => {
    const calculateOrganizationCompletions = async () => {
      if (organizations.length === 0 || programs.length === 0 || groups.length === 0 || allUsers.length === 0) {
        setOrganizationCompletions({});
        return;
      }

      try {
        // Load all patients
        const { getAllPatients } = await import('../firebase');
        const allPatients = await getAllPatients();
        const activePatients = allPatients.filter(p => p.status === 'active');

        if (activePatients.length === 0) {
          setOrganizationCompletions({});
          return;
        }

        // Import form management functions
        const { getAllForms, getCaseSubmissions } = await import('../firebase/formManagement');
        const allForms = await getAllForms();

        const completions: { [organizationId: string]: number } = {};
        const meetingCompletions: { [organizationId: string]: { completed: number; total: number } } = {};

        // Calculate completion for each organization
        for (const org of organizations) {
          // Get all programs belonging to this organization
          const orgPrograms = programs.filter(p => p.organizationId === org.id);
          const orgProgramIds = orgPrograms.map(p => p.id);

          // Get all groups belonging to programs in this organization
          const orgGroups = groups.filter(g => g.programId && orgProgramIds.includes(g.programId));
          const orgGroupIds = orgGroups.map(g => g.id);

          // Get all members related to this organization:
          // 1. Users directly assigned to the organization
          // 2. Users assigned to programs in this organization
          // 3. Users assigned to groups in programs in this organization
          const orgMembers = allUsers.filter(u => 
            u.organizationId === org.id || // Directly assigned to organization
            (u.programId && orgProgramIds.includes(u.programId)) || // Assigned to a program in this org
            (u.groupId && orgGroupIds.includes(u.groupId)) // Assigned to a group in a program in this org
          );
          
          if (orgMembers.length === 0) {
            completions[org.id] = 0;
            meetingCompletions[org.id] = { completed: 0, total: 0 };
            continue;
          }

          // Collect all intake completions and count meetings
          const allIntakeCompletions: number[] = [];
          let totalCompletedMeetings = 0;
          let totalRequiredMeetings = 0;
          const requiredMeetings = org.requiredMeetings || 0;

          // Get all active cases belonging to this organization (regardless of assignment)
          const orgCases = activePatients.filter(p => {
            // Must belong to this organization (directly or through program)
            if (p.organizationId === org.id) {
              return true;
            }
            // Check if patient belongs to a program in this organization
            if (p.programId && orgProgramIds.includes(p.programId)) {
              return true;
            }
            // Check if patient belongs to a group in this organization
            if (p.groupId && orgGroupIds.includes(p.groupId)) {
              return true;
            }
            return false;
          });

          // Calculate intake and count meetings for all cases in this organization
          for (const patient of orgCases) {
            try {
              // Calculate intake completion (only for cases assigned to org members)
              const isAssignedToOrgMember = orgMembers.some(member => 
                Array.isArray(patient.assignedAdmins) && patient.assignedAdmins.includes(member.id)
              );
              
              if (isAssignedToOrgMember) {
                const intakeCompletion = await calculateCaseFormCompletion(patient.caseId);
                // Include all cases, even if completion is 0%
                allIntakeCompletions.push(intakeCompletion);
              }
              
              // Count meetings: completed and total from events DB (for all cases in org)
              const meetingCounts = await countCaseMeetings(patient.caseId);
              totalCompletedMeetings += meetingCounts.completed;
              totalRequiredMeetings += meetingCounts.total;
            } catch (error) {
              console.error(`Error calculating completion for case ${patient.caseId}:`, error);
            }
          }

          // Calculate average intake completion
          if (allIntakeCompletions.length === 0) {
            completions[org.id] = 0;
          } else {
            const totalIntakeCompletion = allIntakeCompletions.reduce((sum, comp) => sum + comp, 0);
            const averageIntakeCompletion = Math.round(totalIntakeCompletion / allIntakeCompletions.length);
            completions[org.id] = averageIntakeCompletion;
          }

          // Set meeting count as completed/all
          meetingCompletions[org.id] = totalRequiredMeetings > 0 
            ? { completed: totalCompletedMeetings, total: totalRequiredMeetings }
            : { completed: 0, total: 0 };
        }

        setOrganizationCompletions(completions);
        setOrganizationMeetingCompletions(meetingCompletions);
      } catch (error) {
        console.error('Error calculating organization completions:', error);
        setOrganizationCompletions({});
        setOrganizationMeetingCompletions({});
      }
    };

    calculateOrganizationCompletions();
  }, [organizations, programs, groups, allUsers]);

  // Search users for administrator selection
  const searchUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        // Exclude deleted users
        if (!userData.deleted && (userData.role === 'admin' || userData.role === 'super_admin')) {
          const user: UserManagementUser = {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role,
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          };
          
          // Filter by search term
          if (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.email.toLowerCase().includes(searchTerm.toLowerCase())) {
            users.push(user);
          }
        }
      });
      
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle user search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(userSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchTerm]);

  // Handle details modal user search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDetailsUsers(detailsUserSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [detailsUserSearchTerm]);

  // Search users for details modal
  const searchDetailsUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setDetailsSearchResults([]);
      return;
    }

    setIsDetailsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.role === 'admin' || userData.role === 'super_admin') {
          const user: UserManagementUser = {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role,
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          };
          
          // Filter by search term and exclude already assigned users
          if ((user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
              !editData.administrators.includes(user.id)) {
            users.push(user);
          }
        }
      });
      
      setDetailsSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsDetailsSearching(false);
    }
  };

  const handleAddAdministrator = (user: UserManagementUser) => {
    if (!formData.administrators.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        administrators: [...prev.administrators, user.id],
        administratorNames: [...prev.administratorNames, user.name]
      }));
    }
    setUserSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveAdministrator = (userId: string) => {
    const index = formData.administrators.indexOf(userId);
    if (index > -1) {
      setFormData(prev => ({
        ...prev,
        administrators: prev.administrators.filter(id => id !== userId),
        administratorNames: prev.administratorNames.filter((_, i) => i !== index)
      }));
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setFormData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Details modal handlers
  const handleOpenDetails = (org: Organization) => {
    // Close other modals first
    setShowWizard(false);
    setShowAddUserModal(false);
    
    setSelectedOrg(org);
    
    // Get all members (users with this organizationId)
    const allMembers = allUsers.filter(u => u.organizationId === org.id);
    const administrators = org.administrators || [];
    
    setEditData({
      name: org.name,
      description: org.description || '',
      logoUrl: org.logoUrl || '',
      administrators: administrators,
      administratorNames: org.administratorNames || [],
      allMembers: allMembers.map(u => u.id) // Store all member IDs
    });
    setDetailsViewMode('view');
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setShowAddUserModal(false);
    setSelectedOrg(null);
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
    setShowAddMember(false);
  };

  const handleAddDetailsAdministrator = (user: UserManagementUser) => {
    if (!editData.administrators.includes(user.id)) {
      setEditData(prev => ({
        ...prev,
        administrators: [...prev.administrators, user.id],
        administratorNames: [...prev.administratorNames, user.name]
      }));
    }
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
  };

  const handleRemoveDetailsAdministrator = async (userId: string) => {
    if (!selectedOrg) return;
    
    try {
      // Remove from organization's administrators array
      const orgRef = doc(db, 'organizations', selectedOrg.id);
      const orgDoc = await getDoc(orgRef);
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        const currentAdministrators = orgData.administrators || [];
        const currentAdministratorNames = orgData.administratorNames || [];
        const index = currentAdministrators.indexOf(userId);
        
        if (index > -1) {
          await updateDoc(orgRef, {
            administrators: currentAdministrators.filter((id: string) => id !== userId),
            administratorNames: currentAdministratorNames.filter((_: string, i: number) => i !== index)
          });
        }
      }
      
      // Update local state
      const index = editData.administrators.indexOf(userId);
      if (index > -1) {
        setEditData(prev => ({
          ...prev,
          administrators: prev.administrators.filter(id => id !== userId),
          administratorNames: prev.administratorNames.filter((_, i) => i !== index)
        }));
      }
    } catch (error) {
      console.error('Error removing administrator:', error);
      alert(t('organizations.removeError') || 'Failed to remove administrator');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedOrg) return;
    
    try {
      // Remove user's organizationId (remove from organization)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizationId: null
      });
      
      // If user was also an administrator, remove from administrators array
      if (editData.administrators.includes(userId)) {
        await handleRemoveDetailsAdministrator(userId);
      }
      
      // Reload users to refresh the list
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (!userData.deleted) {
          users.push({
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role,
            organizationId: userData.organizationId,
            organizationRole: userData.organizationRole,
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          });
        }
      });
      setAllUsers(users);
      
      // Update editData
      const allMembers = users.filter(u => u.organizationId === selectedOrg.id);
      const updatedOrg = organizations.find(o => o.id === selectedOrg.id);
      if (updatedOrg) {
        setEditData(prev => ({
          ...prev,
          allMembers: allMembers.map(u => u.id)
        }));
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert(t('organizations.removeError') || 'Failed to remove member');
    }
  };

  const handleDetailsLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setEditData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleUpdateOrganization = async () => {
    if (!selectedOrg || !editData.name.trim()) return;

    setIsUpdating(true);
    try {
      const orgRef = doc(db, 'organizations', selectedOrg.id);
      await updateDoc(orgRef, {
        name: editData.name.trim(),
        description: editData.description.trim(),
        logoUrl: editData.logoUrl,
        administrators: editData.administrators,
        administratorNames: editData.administratorNames,
        updatedAt: serverTimestamp(),
      });
      
      handleCloseDetails();
    } catch (error) {
      console.error('Error updating organization:', error);
      console.error('Update error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;

    // Delete organization

    setIsDeleting(true);
    try {
      const orgRef = doc(db, 'organizations', selectedOrg.id);
      await deleteDoc(orgRef);
      
      handleCloseDetails();
    } catch (error) {
      console.error('Error deleting organization:', error);
      console.error('Delete error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!formData.name.trim()) return;

    setIsCreating(true);
    try {
      await addDoc(collection(db, 'organizations'), {
        name: formData.name.trim(),
        description: formData.description.trim(),
        logoUrl: formData.logoUrl,
        administrators: formData.administrators,
        administratorNames: formData.administratorNames,
        createdBy: user.name,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
      });
      
      // Reset form and close wizard
      setFormData({
        name: '',
        description: '',
        logoUrl: '',
        administrators: [],
        administratorNames: []
      });
      setShowWizard(false);
      setShowDetailsModal(false);
      setShowAddUserModal(false);
      setCurrentStep('basic');
    } catch (error) {
      console.error('Error creating organization:', error);
      console.error('Create error');
    } finally {
      setIsCreating(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 'basic' && formData.name.trim()) {
      setCurrentStep('administrators');
    } else if (currentStep === 'administrators') {
      setCurrentStep('details');
    }
  };

  const prevStep = () => {
    if (currentStep === 'details') {
      setCurrentStep('administrators');
    } else if (currentStep === 'administrators') {
      setCurrentStep('basic');
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return t('organizations.na');
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } catch {
      return t('organizations.na');
    }
  };

  const renderWizardStep = () => {
    switch (currentStep) {
      case 'basic':
        return (
          <div>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {t('organizations.wizard.basicInfo')}
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.organizationName')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('organizations.namePlaceholder')}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        );

      case 'administrators':
        return (
          <div>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {t('organizations.wizard.selectAdministrators')}
            </h3>
            
            {/* Selected Administrators */}
            {formData.administrators.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('organizations.wizard.selectedAdministrators')}:
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {formData.administratorNames.map((name, index) => (
                    <div
                      key={formData.administrators[index]}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#e3f2fd',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '14px'
                      }}
                    >
                      {name}
                      <button
                        type="button"
                        onClick={() => handleRemoveAdministrator(formData.administrators[index])}
                        style={{
                          marginLeft: '8px',
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Search */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.searchUsers')}:
              </label>
              <input
                type="text"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                placeholder={t('organizations.wizard.searchUsersPlaceholder')}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {searchResults.map((searchUser) => (
                    <div
                      key={searchUser.id}
                      onClick={() => handleAddAdministrator(searchUser)}
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '500' }}>{searchUser.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{searchUser.email}</div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {searchUser.role === 'super_admin' ? t('users.roles.super_admin') : t('users.roles.admin')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {isSearching && (
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {t('organizations.wizard.searching')}...
                </div>
              )}
            </div>
          </div>
        );

      case 'details':
        return (
          <div>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {t('organizations.wizard.logoAndDescription')}
            </h3>
            
            {/* Logo Upload */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.logo')}:
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleLogoUpload}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {isUploading && (
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {t('organizations.wizard.uploading')}...
                </div>
              )}
              {formData.logoUrl && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    key={formData.logoUrl}
                    src={formData.logoUrl}
                    alt="Organization logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                    onLoad={() => setIsUploading(false)}
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.description')}:
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('organizations.wizard.descriptionPlaceholder')}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: '#333' }}>{t('organizations.title')}</h2>
        <button
          onClick={() => {
            // Close other modals first
            setShowDetailsModal(false);
            setShowAddUserModal(false);
            setShowWizard(true);
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007acc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '18px' }}>+</span>
          {t('organizations.createNew')}
        </button>
      </div>

      {/* Create Organization Wizard */}
      {showWizard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
            <button
              onClick={() => {
                setShowWizard(false);
                setShowDetailsModal(false);
                setShowAddUserModal(false);
                setCurrentStep('basic');
                setFormData({
                  name: '',
                  description: '',
                  logoUrl: '',
                  administrators: [],
                  administratorNames: []
                });
              }}
              style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { left: '15px' } : { right: '15px' }),
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#666',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                padding: 0,
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('organizations.close') || 'Close'}
            >
              ×
            </button>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
              {t('organizations.createNew')}
            </h3>

            {/* Progress Indicator */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                {['basic', 'administrators', 'details'].map((step, index) => (
                  <div
                    key={step}
                    style={{
                      flex: 1,
                      height: '4px',
                      backgroundColor: currentStep === step || 
                        (step === 'basic') ||
                        (step === 'administrators' && (currentStep === 'administrators' || currentStep === 'details')) ||
                        (step === 'details' && currentStep === 'details')
                        ? '#007acc' : '#e9ecef',
                      marginRight: index < 2 ? '8px' : 0,
                      borderRadius: '2px'
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                {t(`organizations.wizard.step${currentStep === 'basic' ? '1' : currentStep === 'administrators' ? '2' : '3'}of3`)}
              </div>
            </div>

            {renderWizardStep()}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                {currentStep !== 'basic' && (
                  <button
                    onClick={prevStep}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {t('organizations.wizard.back')}
                  </button>
                )}
                {currentStep === 'details' ? (
                  <button
                    onClick={handleCreateOrganization}
                    disabled={isCreating || !formData.name.trim()}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isCreating ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      opacity: isCreating ? 0.6 : 1
                    }}
                  >
                    {isCreating ? t('organizations.creating') : t('organizations.wizard.create')}
                  </button>
                ) : (
                  <button
                    onClick={nextStep}
                    disabled={currentStep === 'basic' && !formData.name.trim()}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (currentStep === 'basic' && !formData.name.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      opacity: (currentStep === 'basic' && !formData.name.trim()) ? 0.6 : 1
                    }}
                  >
                    {t('organizations.wizard.next')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Details Modal */}
      {showDetailsModal && selectedOrg && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
            <button
              onClick={handleCloseDetails}
              style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { right: '15px' } : { left: '15px' }),
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#666',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                padding: 0,
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('organizations.close') || 'Close'}
            >
              ×
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', paddingRight: '40px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>
                {selectedOrg.name}
              </h3>
              {detailsViewMode === 'view' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => {
                      // Close all other modals first
                      setShowWizard(false);
                      setShowDetailsModal(false);
                      setShowAddUserModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('users.addUser') || 'Add User'}
                  </button>
                  <button
                    onClick={() => setDetailsViewMode('edit')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('common.edit') || 'Edit'}
                  </button>
                </div>
              )}
              {detailsViewMode === 'edit' && (
                <button
                  onClick={() => setDetailsViewMode('view')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('common.back') || 'Back'}
                </button>
              )}
            </div>

            {detailsViewMode === 'view' ? (
              /* Members View */
              <div>
                {/* Get all members for this organization */}
                {(() => {
                  const allMembers = allUsers.filter(u => u.organizationId === selectedOrg?.id);
                  const administrators = editData.administrators || [];
                  const regularMembers = allMembers.filter(u => !administrators.includes(u.id));
                  
                  return (
                    <>
                      {/* Administrators Section */}
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('organizations.administrators') || 'Administrators'}
                        </h4>
                        {administrators.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No administrators'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {administrators.map((adminId) => {
                              const member = allUsers.find(u => u.id === adminId);
                              if (!member) return null;
                              
                              return (
                                <div
                                  key={adminId}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveDetailsAdministrator(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('organizations.removeAdministrator') || 'Remove Administrator'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {t('users.roles.admin') || 'Administrator'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Regular Members Section */}
                      <div>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('organizations.members') || 'Members'}
                        </h4>
                        {regularMembers.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No members'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {regularMembers.map((member) => {
                              return (
                                <div
                                  key={member.id}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveMember(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('organizations.removeMember') || 'Remove Member'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {member.organizationRole || t('users.roles.social_worker') || 'Member'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              /* Edit View */
              <>
            {/* Organization Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.organizationName')} *
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.description')}:
              </label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('organizations.wizard.descriptionPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Logo */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('organizations.wizard.logo')}:
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleDetailsLogoUpload}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {isUploading && (
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {t('organizations.wizard.uploading')}...
                </div>
              )}
              {editData.logoUrl && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    key={editData.logoUrl}
                    src={editData.logoUrl}
                    alt="Organization logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                    onLoad={() => setIsUploading(false)}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
              <button
                onClick={handleDeleteOrganization}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#dc3545',
                  color: 'white',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                {isDeleting ? t('organizations.details.deleting') : t('organizations.details.delete')}
              </button>
              <button
                onClick={handleUpdateOrganization}
                disabled={isUpdating || !editData.name.trim()}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007acc',
                  color: 'white',
                  cursor: (isUpdating || !editData.name.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: (isUpdating || !editData.name.trim()) ? 0.6 : 1
                }}
              >
                {isUpdating ? t('organizations.details.updating') : t('organizations.details.update')}
              </button>
            </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Organizations Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('organizations.loading')}
        </div>
      ) : organizations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('organizations.noOrganizations')}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {organizations.map((org) => {
            const isRTL = i18n.language === 'he';
            return (
            <div
              key={org.id}
              onClick={() => handleOpenDetails(org)}
              style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  height: '100%'
              }}
              onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
                {/* Content Section */}
                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ 
                      margin: '0 0 10px 0', 
                      color: '#1e293b',
                      fontSize: '20px',
                      fontWeight: '600',
                      lineHeight: '1.3'
                  }}>
                    {org.name}
                  </h3>
                  {org.description && (
                    <p style={{
                        margin: '0 0 14px 0',
                        color: '#64748b',
                      fontSize: '14px',
                        lineHeight: '1.5',
                        flex: 1
                    }}>
                      {org.description}
                    </p>
                  )}

                  {/* Completion Statistics */}
                  <div style={{
                    marginTop: 'auto',
                    paddingTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {/* Intake Completion */}
                    {organizationCompletions[org.id] !== undefined && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '11px', color: '#666', minWidth: '50px' }}>
                          {i18n.language === 'he' ? 'קליטה:' : 'Intake:'}
                        </span>
                        <div style={{
                          width: '60px',
                          height: '6px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <div
                            style={{
                              width: `${organizationCompletions[org.id]}%`,
                              height: '100%',
                              backgroundColor: getCompletionColor(organizationCompletions[org.id]),
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: getCompletionColor(organizationCompletions[org.id]),
                          minWidth: '35px'
                        }}>
                          {organizationCompletions[org.id]}%
                        </span>
                      </div>
                    )}
                  </div>
              
              </div>
              
              {/* Image Section - Right side */}
              <div style={{
                width: '200px',
                minWidth: '200px',
                height: '100%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                {org.logoUrl ? (
                  <img
                    src={org.logoUrl}
                    alt={`${org.name} logo`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '64px',
                    color: '#6b7280'
                  }}>
                    🏢
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Add User Modal */}
      {selectedOrg && (
        <AddUserModal
          user={user}
          isOpen={showAddUserModal}
          onClose={async () => {
            setShowAddUserModal(false);
            // Refresh the organization data when modal closes
            if (selectedOrg) {
              // Reload users to get updated organizationId assignments
              try {
                const usersRef = collection(db, 'users');
                const snapshot = await getDocs(usersRef);
                const users: UserManagementUser[] = [];
                
                snapshot.forEach((doc) => {
                  const userData = doc.data();
                  if (!userData.deleted) {
                    users.push({
                      id: doc.id,
                      name: userData.name || '',
                      email: userData.email || '',
                      role: userData.role,
                      organizationId: userData.organizationId,
                      organizationRole: userData.organizationRole,
                      createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
                      lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
                      loginCount: userData.loginCount || 0,
                      blocked: userData.blocked || false
                    });
                  }
                });
                setAllUsers(users);
                
                // Update selected org and edit data
                const updatedOrg = organizations.find(o => o.id === selectedOrg.id);
                if (updatedOrg) {
                  const allMembers = users.filter(u => u.organizationId === updatedOrg.id);
                  setSelectedOrg(updatedOrg);
                  setEditData({
                    name: updatedOrg.name,
                    description: updatedOrg.description || '',
                    logoUrl: updatedOrg.logoUrl || '',
                    administrators: updatedOrg.administrators || [],
                    administratorNames: updatedOrg.administratorNames || [],
                    allMembers: allMembers.map(u => u.id)
                  });
                }
              } catch (error) {
                console.error('Error loading users:', error);
              }
            }
          }}
          organizationId={selectedOrg.id}
          entityType="organization"
          entityName={selectedOrg.name}
        />
      )}
    </div>
  );
}